import type { AuthUserDto, JWT } from '@/core/data/auth/auth.interface';
import type { StorageRepositoryInterface } from '@/core/data/storage/storage.interface';
import { StorageKeyType } from '@/core/data/storage/storage.interface';
import { clearHandoffCookie } from '@/core/helpers/handoff-cookie.helper';
import { AxiosHeaders } from 'axios';

export class JwtRepository {
  private readonly storage: StorageRepositoryInterface;
  private readonly session: StorageRepositoryInterface;

  constructor(storage: StorageRepositoryInterface, session: StorageRepositoryInterface) {
    this.storage = storage;
    this.session = session;
  }

  getJWToken(): JWT | null {
    return this.storage.get<JWT>(StorageKeyType.JWToken);
  }

  getUser(): AuthUserDto | null {
    return this.storage.get<AuthUserDto | null>(StorageKeyType.User);
  }

  setJWToken(tokenInfo: JWT): void {
    const payloadInfo = this.parseJwt(tokenInfo.accessToken);

    // Store JWT data to storage
    this.storage.set<JWT>(StorageKeyType.JWToken, tokenInfo as JWT);
    this.storage.set<AuthUserDto>(StorageKeyType.User, payloadInfo);
  }

  parseJwt(token: string): AuthUserDto {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join(''),
    );
    return JSON.parse(jsonPayload) as AuthUserDto;
  }

  setHeadersFromJWT(): AxiosHeaders {
    const headers: AxiosHeaders = new AxiosHeaders();

    // Set Authorization header
    const jwt = this.getJWToken();
    if (jwt) {
      headers.authorization = `Bearer ${jwt.accessToken}`;
    }

    return headers;
  }

  async logout(): Promise<void> {
    this.storage.remove(StorageKeyType.JWToken);
    this.storage.remove(StorageKeyType.User);
    // UserRepository caches this in the session store, not alongside the token,
    // so removing it from local storage here quietly did nothing and the profile
    // outlived the sign out. It only ever surfaced when the next sign in failed
    // to fetch a profile, which leaves the cache untouched - the incoming user
    // then reads the outgoing one's name and avatar.
    this.session.remove(StorageKeyType.UserInfo);
    // Every route that drops the token lands here, including a refresh that came
    // back 401 because the session was revoked elsewhere. Leaving the parked
    // hand-off code behind would let the next launch redeem it and rebuild the
    // session that was just revoked. Server-side revocation is the caller's job
    // where a token is still available; by here there is nothing left to
    // authenticate with, so the code is simply put out of reach until it expires.
    clearHandoffCookie();
  }
}
